'use client'
import { useState, useRef, useEffect, useMemo } from 'react'
import areasData from '../../data/suburb-list.json'

const allSuburbs = Object.values(areasData.regions).flat().map(s => s.name).sort()

export default function SuburbAutocomplete({ value, onChange, placeholder = 'Start typing your suburb...', name }) {
    const [open, setOpen] = useState(false)
    const [highlight, setHighlight] = useState(0)
    const [query, setQuery] = useState(value || '')
    const wrapperRef = useRef(null)

    useEffect(() => { setQuery(value || '') }, [value])

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const results = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return []
        return allSuburbs.filter(s => s.toLowerCase().includes(q)).slice(0, 8)
    }, [query])

    function selectSuburb(suburb) {
        onChange(suburb)
        setQuery(suburb)
        setOpen(false)
    }

    function handleKeyDown(e) {
        if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            if (results.length) setOpen(true)
            return
        }
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)) }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)) }
        else if (e.key === 'Enter' && open && results[highlight]) { e.preventDefault(); selectSuburb(results[highlight]) }
        else if (e.key === 'Escape') { setOpen(false) }
    }

    const inputStyle = {
        width: '100%',
        padding: '0.8rem',
        border: '1px solid var(--gray-300)',
        borderRadius: 'var(--border-radius)',
        fontSize: '1rem'
    }

    return (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
            <input
                type="text"
                name={name}
                value={query}
                autoComplete="off"
                onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); setHighlight(0) }}
                onFocus={() => { if (query.trim()) setOpen(true) }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                style={inputStyle}
            />
            {open && results.length > 0 && (
                <div style={{
                    position: 'absolute',
                    zIndex: 50,
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.25rem',
                    background: 'white',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 'var(--border-radius)',
                    boxShadow: 'var(--shadow-lg)',
                    maxHeight: '240px',
                    overflowY: 'auto'
                }}>
                    {results.map((suburb, i) => (
                        <div
                            key={suburb}
                            onMouseDown={(e) => { e.preventDefault(); selectSuburb(suburb) }}
                            onMouseEnter={() => setHighlight(i)}
                            style={{
                                padding: '0.6rem 0.8rem',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                background: i === highlight ? 'var(--primary)' : 'white',
                                color: i === highlight ? 'white' : 'inherit',
                                borderBottom: '1px solid var(--gray-100)'
                            }}
                        >
                            {suburb}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}